<template>
    <div
        :class="[
            prefix ? 'has-prefix' : '',
            state.hasContent ? 'has-content' : '',
            type === 'textarea' ? 'pt-textarea' : 'pt-input-v1',
            size ? 'pt-input-v1--' + size : '',
            state.invalid ? 'warning' : '',
            state.isFocus ? 'focus' : '',
            'pt-input-v1_' + inputStyle,
            color ? 'pt-input-v1_' + color : '',
            {
                'is-disabled': disabled
            }
        ]"
    >
        <span class="pt-input-v1__prefix" v-if="prefix && prefixIcon" @click.stop="togglePrefix">
            <pt-icon v-if="prefix && prefixIcon" :icon="prefixIcon"></pt-icon>
        </span>

        <!-- TODO: v-show="inputStyle === 'underline' ? state.prefixClicked || state.hasContent : true" -->
        <input
            v-if="type !== 'textarea'"
            class="pt-input-v1__inner"
            :class="{
                hasContent: state.hasContent,
                'pt-input-v1__inner--clearButton': clearButton
            }"
            :type="type"
            :name="name"
            :id="id"
            :placeholder="placeholder"
            :disabled="disabled"
            :readonly="readonly"
            :maxlength="maxlength"
            :minlength="minlength"
            :autocomplete="autoComplete"
            :min="min"
            :max="max"
            :value="state.currentValue"
            ref="input"
            @keydown="handleEnter"
            @input="handleInput"
            @focus="handleFocus"
            @blur="handleBlur"
        />

        <div v-else class="pt-textarea__container_parent">
            <div class="pt-textarea__container" ref="textarea__container">
                <textarea
                    class="pt-textarea__inner"
                    :class="{ hasContent: state.hasContent }"
                    :value="state.currentValue"
                    ref="textarea"
                    :name="name"
                    :id="id"
                    :rows="rows"
                    :placeholder="placeholder"
                    :disabled="disabled"
                    :readonly="readonly"
                    :maxlength="maxlength"
                    :minlength="minlength"
                    @input="handleInput"
                    @focus="handleFocus"
                    @blur="handleBlur"
                ></textarea>
            </div>
        </div>

        <span
            class="pt-input-v1__inner__clearButton"
            v-if="clearButton && state.currentValue"
            @click="handleClearButtonClick"
        >
            <pt-icon icon="pt-icon--clear"></pt-icon>
        </span>
        <p :class="['pt-input-v1__warning', errMsgPosition ? `position-${errMsgPosition}` : '']">
            <span v-html="state.invalidTip"></span>
        </p>

        <span class="pt-input-v1__suffix" v-if="showWordLimit">
            <span class="pt-input-v1__suffix-inner">
                <span class="el-input__count">
                    <span class="el-input__count-inner">{{
                        `${state.currentValue.length}/${maxlength}`
                    }}</span>
                </span>
            </span>
        </span>
    </div>
</template>

<script lang="ts">
import { defineComponent, reactive, watch, ref, onMounted, nextTick } from 'vue';
import PtIcon from '../../icon';
type Size = 'default' | 'big' | 'large' | 'xlarge';
type Color = 'green';
type Style = 'border' | 'underline' | 'line';
type EmitFn = (evt: Event) => void;
export interface Props {
    color?: Color;
    isWatchInput?: boolean;
    modelValue: [string, number];
    placeholder?: string;
    size?: Size;
    readonly?: boolean;
    autofocus?: boolean;
    disabled?: boolean;
    type?: string;
    inputStyle?: Style; //有边框
    name?: string;
    id?: string;
    rows?: number;
    autoComplete?: string;
    maxlength?: number;
    minlength?: number;
    showWordLimit?: boolean;
    max?: Object;
    min?: Object;
    warning?: boolean; //错误样式
    warningText?: string; // 错误提示
    prefix?: boolean;
    //  prefixText: string;
    prefixIcon?: string;
    clearButton?: boolean | Function;
    rules?: Array<Object>;
    enableEnter?: boolean;
    errMsgPosition?: string;
}
const props = {
    color: {
        type: String
    },
    isWatchInput: {
        type: Boolean,
        default: true
    },
    modelValue: [String, Number],
    placeholder: String,
    size: {
        type: String,
        default: 'default'
    },
    readonly: Boolean,
    autofocus: {
        type: Boolean,
        default: false
    },
    disabled: Boolean,
    type: {
        type: String,
        default: 'text'
    },
    inputStyle: {
        type: String,
        default: 'border'
    }, //有边框
    name: String,
    id: String,
    rows: {
        type: Number,
        default: () => 2
    },
    autoComplete: {
        type: String,
        default: 'on'
    },
    maxlength: Number,
    minlength: Number,
    showWordLimit: {
        type: Boolean,
        default: function () {
            return false;
        }
    },
    max: {},
    min: {},
    warning: Boolean, //错误样式
    warningText: String, // 错误提示
    prefix: {
        type: Boolean,
        default: false
    },
    //  prefixText: String,
    prefixIcon: String,
    clearButton: {
        type: Boolean || Function,
        default: false
    },
    rules: {
        type: Array
    },
    enableEnter: {
        type: Boolean,
        default: false
    },
    errMsgPosition: {
        type: String,
        default: 'bottom'
    }
};
export default defineComponent({
    name: 'ptInputV1',
    props,
    components: { PtIcon },
    emits: ['keydown', 'input', 'focus', 'blur', 'update:modelValue', 'enter', 'change'],
    setup(props: Props, { emit }) {
        // const { value, warning, warningText } = toRefs(props);
        const input = ref(null);
        const textarea = ref(null);
        const textarea__container = ref(null);
        const state = reactive({
            currentValue: props.modelValue,
            hasContent: Boolean(props.modelValue),
            last: 0,
            isFocus: false,
            invalid: props.warning,
            prefixClicked: false,
            invalidTip: props.warningText,
            clickPrefix: false
        });

        watch(
            () => props.modelValue,
            (val: [String, Number]) => {
                setCurrentValue(val, 'watch');
            }
        );
        watch(
            () => props.warning,
            val => {
                state.invalid = val;
            }
        );
        watch(
            () => props.warningText,
            val => {
                state.invalidTip = val;
            }
        );
        onMounted(() => {});
        return {
            state,
            handleFocus,
            handleEnter,
            handleClearButtonClick,
            togglePrefix,
            handleClickoutside,
            handleInput,
            focus,
            showError,
            clearError,
            handleBlur,
            input,
            textarea,
            textarea__container
        };
        function handleClearButtonClick() {
            const isFunction = function (functionToCheck: any): Boolean {
                return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
            };
            if (isFunction(props.clearButton)) {
                (props.clearButton as Function)();
            }
            setCurrentValue('', 'input');
        }
        function checkText() {
            if (props.type === 'textarea') {
                const textarea: any = textarea.value;
                // if (textarea && textarea.clientHeight < textarea.scrollHeight) {
                //     this.$refs.textarea__scroll.style.display = 'block';
                // } else {
                //     this.$refs.textarea__scroll.style.display = 'none';
                // }
            }
        }

        function handleEnter(event: {
            keyCode: any;
            which: any;
            target: { blur: () => void };
        }): void {
            if ((window.event ? event.keyCode : event.which) === 13) {
                event.target.blur();
                emit('enter', event);
            }
        }
        function handleClickoutside(): void {
            nextTick(() => {
                state.clickPrefix = false;
                if (!state.clickPrefix) {
                    state.prefixClicked = false;
                }
            });
        }
        function togglePrefix(event: any): void {
            state.prefixClicked = !state.prefixClicked;
            state.clickPrefix = true;
            nextTick(() => {
                if (state.prefixClicked) {
                    handleFocus(event);
                }
            });
        }
        function handleFocus(event: { target: { value: any } }) {
            state.invalid = false;
            state.invalidTip = '';
            state.isFocus = true;
            checkHasConten(event.target.value, 'focus');
            if (props.type === 'text' && props.prefix && props.prefixIcon) {
                input.value?.focus();
            }
            emit('focus', event);
        }
        function handleInput(event: { target: { value: any } }): void {
            setCurrentValue(event.target.value, 'input');
            nextTick(() => {
                checkText();
            });
        }

        function handleBlur(event: { target: { value: any } }) {
            state.isFocus = false;
            checkHasConten(event.target.value, 'blur');
            emit('blur', event);
        }

        function checkHasConten(value: any, type: string) {
            state.hasContent = !(type === 'focus' || !value);
        }

        // 暴露给外部的可以使pt-input-v1获得焦点
        function focus(): void {
            if (props.type === 'textarea') {
                textarea.value?.focus();
            } else {
                input.value?.focus();
            }
        }
        function showError(msg: string) {
            state.invalid = true;
            state.invalidTip = msg;
        }
        function clearError() {
            state.invalid = false;
            state.invalidTip = '';
        }

        function setCurrentValue(value: [String, Number], type: string) {
            if (value === state.currentValue) return;
            // if (type === 'watch') this.checkHasConten(value, type)
            state.currentValue = value;
            checkHasConten(value, type);
            emit('update:modelValue', value);
            emit('change', value);
        }
    }
});
</script>

<style lang="scss">
@import './input.scss';
</style>
